/**
 * This file includes polyfills needed by Angular and is loaded before the app.
 * You can add your own extra polyfills to this file.
 *
 * This file is divided into 2 sections:
 *   1. Browser polyfills. These are applied before loading the app and are sorted by browsers.
 *   2. Application imports. Files imported before your main file.
 *
 * The current setup is for so-called "evergreen" browsers; the last versions of browsers that
 * automatically update themselves. This includes Safari >= 10, Chrome >= 55 (including Opera),
 * Edge >= 13 on the desktop, and iOS 10 and Chrome on mobile.
 *
 * Learn more in https://angular.io/guide/browser-support
 */

/***************************************************************************************************
 * BROWSER POLYFILLS
 */

/** IE10 and IE11 requires the following for NgClass support on SVG elements */
import 'classlist.js'; // Run `npm install --save classlist.js`.

/** Evergreen browsers require these. **/
// import 'core-js/es6/reflect'; // No longer required
// import 'core-js/es7/reflect'; // No longer required

/** Legacy Firefox browsers required additional animation polyfills. **/
const globalProcess = (globalThis as { process?: { versions?: { node?: string } } }).process;
const isJsDomEnvironment =
  (typeof navigator !== 'undefined' &&
    typeof navigator.userAgent === 'string' &&
    navigator.userAgent.includes('jsdom')) ||
  !!globalProcess?.versions?.node;

type StubCanvasContext = Partial<CanvasRenderingContext2D>;

function noop(): void {
  return undefined;
}

function createImageDataStub(): ImageData {
  return {
    data: new Uint8ClampedArray(),
    width: 0,
    height: 0,
    colorSpace: 'srgb',
  } as ImageData;
}

function measureTextStub(): TextMetrics {
  return { width: 0 } as TextMetrics;
}

function createCanvasContextStub(): StubCanvasContext {
  return {
    fillRect: noop,
    clearRect: noop,
    getImageData: createImageDataStub,
    putImageData: noop,
    createImageData: createImageDataStub,
    setTransform: noop,
    drawImage: noop,
    save: noop,
    fillText: noop,
    restore: noop,
    beginPath: noop,
    moveTo: noop,
    lineTo: noop,
    closePath: noop,
    stroke: noop,
    translate: noop,
    scale: noop,
    rotate: noop,
    arc: noop,
    fill: noop,
    measureText: measureTextStub,
    transform: noop,
    rect: noop,
    clip: noop,
  };
}

if (isJsDomEnvironment && typeof HTMLCanvasElement !== 'undefined') {
  Object.defineProperty(HTMLCanvasElement.prototype, 'getContext', {
    configurable: true,
    value: createCanvasContextStub,
  });
}

/***************************************************************************************************
 * APPLICATION IMPORTS
 */

// (window as any).global = window; // This is generally no longer needed.

// Third party JS imports
// Page loader
import './vendor/pace/pace.min.js';
// // Charts
// import './vendor/Chart.min.js';
// // User tour
import 'hopscotch';

/**
 * Date, currency, decimal and percent pipes.
 * Needed for: All but Chrome, Firefox, Edge, IE11 and Safari 10
 */
// import 'intl';  // Run `npm install --save intl`.
/**
 * Need to import at least one locale-data with intl.
 */
// import 'intl/locale-data/jsonp/en';
